import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { renderTermsMarkdown } from "./markdown";
import { ensureModalRoot } from "./modal-root";
const AUDIENCE_LABEL = {
    individual: 'patients',
    consultant: 'consultants',
    store: 'stores'
};
export const TermsGate = ({ audience, accessToken, baseURL, onDecline, onAccepted })=>{
    const [terms, setTerms] = useState(null);
    const [status, setStatus] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const [portalEl, setPortalEl] = useState(null);
    // Create / lookup the stable portal target after mount — never during render.
    useEffect(()=>{
        setPortalEl(ensureModalRoot());
    }, []);
    const refresh = useCallback(async ()=>{
        if (!accessToken) return;
        try {
            const [termsRes, statusRes] = await Promise.all([
                fetch(`${baseURL}/auth/terms?audience=${encodeURIComponent(audience)}`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                }),
                fetch(`${baseURL}/auth/terms/status?audience=${encodeURIComponent(audience)}`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                })
            ]);
            if (termsRes.ok) setTerms(await termsRes.json());
            if (statusRes.ok) setStatus(await statusRes.json());
        } catch  {
        /* network hiccup — try again on next mount */ }
    }, [
        accessToken,
        audience,
        baseURL
    ]);
    useEffect(()=>{
        refresh();
    }, [
        refresh
    ]);
    const needsConsent = !!status && !status.has_accepted_current && !!terms;
    const isBlocking = needsConsent && !!status?.requires_reacceptance;
    const isDismissable = needsConsent && !isBlocking;
    const dismissKey = useMemo(()=>terms?.id != null ? `terms-dismissed:${audience}:${terms.id}` : `terms-dismissed:${audience}:${terms?.version ?? ''}`, [
        terms?.id,
        terms?.version,
        audience
    ]);
    useEffect(()=>{
        if (!isDismissable) return;
        try {
            if (typeof window !== 'undefined' && window.localStorage.getItem(dismissKey) === '1') {
                setDismissed(true);
            }
        } catch  {
        /* ignore */ }
    }, [
        dismissKey,
        isDismissable
    ]);
    const shouldShow = needsConsent && !(isDismissable && dismissed);
    // We deliberately do NOT toggle document.body.style.overflow here. Locking
    // body scroll while a fixed-position modal is open causes a scrollbar
    // shift on Windows/Linux laptops (viewport widens then shrinks back when
    // the modal closes) and fights Ionic's IonContent scroll management. The
    // backdrop already catches all clicks behind the modal.
    const handleAccept = async ()=>{
        if (!terms || !accessToken) return;
        setSubmitting(true);
        setError(null);
        try {
            const res = await fetch(`${baseURL}/auth/terms/accept`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    audience,
                    terms_document_id: terms.id ?? undefined,
                    version: terms.version
                })
            });
            if (!res.ok) {
                const j = await res.json().catch(()=>({}));
                throw new Error(j?.message || `Accept failed (${res.status})`);
            }
            await refresh();
            onAccepted?.();
            // Belt-and-suspenders: fire a synthetic resize so Ionic's IonContent
            // and any ResizeObserver consumers recompute their layout after the
            // modal unmounts. On some Ionic + framer-motion + portal combinations,
            // the observer can miss the tick.
            if (typeof window !== 'undefined') {
                requestAnimationFrame(()=>window.dispatchEvent(new Event('resize')));
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Accept failed');
        } finally{
            setSubmitting(false);
        }
    };
    const handleDecline = ()=>onDecline?.();
    const handleDismiss = ()=>{
        try {
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(dismissKey, '1');
            }
        } catch  {
        /* ignore */ }
        setDismissed(true);
    };
    const html = useMemo(()=>terms ? renderTermsMarkdown(terms.content_markdown) : '', [
        terms
    ]);
    const modal = shouldShow && terms ? /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 z-[2147483600] flex items-end sm:items-center sm:justify-center bg-black/55 backdrop-blur-sm p-0 sm:p-4 pointer-events-auto",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full sm:max-w-lg max-h-[88vh] sm:max-h-[80vh] overflow-hidden bg-white dark:bg-[#111] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col border border-gray-200 dark:border-gray-800",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "px-5 pt-5 pb-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#111]",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-start gap-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-950 text-primary",
                                children: /*#__PURE__*/ _jsxs("svg", {
                                    viewBox: "0 0 24 24",
                                    fill: "none",
                                    className: "h-5 w-5",
                                    stroke: "currentColor",
                                    strokeWidth: 2,
                                    strokeLinecap: "round",
                                    strokeLinejoin: "round",
                                    "aria-hidden": "true",
                                    children: [
                                        /*#__PURE__*/ _jsx("path", {
                                            d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                                        }),
                                        /*#__PURE__*/ _jsx("polyline", {
                                            points: "14 2 14 8 20 8"
                                        }),
                                        /*#__PURE__*/ _jsx("line", {
                                            x1: "8",
                                            y1: "13",
                                            x2: "16",
                                            y2: "13"
                                        }),
                                        /*#__PURE__*/ _jsx("line", {
                                            x1: "8",
                                            y1: "17",
                                            x2: "13",
                                            y2: "17"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "font-semibold text-base sm:text-lg text-gray-900 dark:text-white truncate",
                                        children: terms.title
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "mt-0.5 text-xs text-gray-500 dark:text-gray-400",
                                        children: [
                                            isBlocking ? 'Updated agreement — please review and accept to continue' : `We’ve updated our terms for ${AUDIENCE_LABEL[audience]}`,
                                            ' ',
                                            "· v",
                                            terms.version
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "px-5 py-4 overflow-y-auto flex-1 text-sm bg-white dark:bg-[#111]",
                    dangerouslySetInnerHTML: {
                        __html: html
                    }
                }),
                error && /*#__PURE__*/ _jsx("div", {
                    className: "px-5 py-2 text-xs text-red-700 bg-red-50 border-t border-red-200",
                    children: error
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-[#111] flex flex-col-reverse sm:flex-row gap-2 sm:justify-end",
                    children: [
                        isBlocking ? /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: handleDecline,
                            disabled: submitting,
                            className: "px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50",
                            children: "Decline & sign out"
                        }) : /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: handleDismiss,
                            disabled: submitting,
                            className: "px-4 py-2 text-sm font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50",
                            children: "Remind me later"
                        }),
                        /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            onClick: handleAccept,
                            disabled: submitting,
                            className: "px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2",
                            children: [
                                submitting && /*#__PURE__*/ _jsx("span", {
                                    className: "h-3.5 w-3.5 rounded-full border-2 border-white/60 border-t-white animate-spin"
                                }),
                                submitting ? 'Saving…' : 'I accept'
                            ]
                        })
                    ]
                })
            ]
        })
    }) : null;
    if (!portalEl) return null;
    return /*#__PURE__*/ createPortal(modal, portalEl);
};
export default TermsGate;

//# sourceMappingURL=TermsGate.js.map